import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Business as BuildingIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface SchoolDetail {
  school_name: string;
  school_registration_number: string;
  school_type: string;
  district: string;
  region: string;
  establishment_year: number;
  total_students: number;
  total_teachers: number;
  student_teacher_ratio: number;
  education_level: string;
  school_age: number;
}

interface DistrictBreakdown {
  district_name: string;
  region_name: string;
  total_schools: number;
  government_schools: number;
  private_schools: number;
  total_students: number;
  total_teachers: number;
  student_teacher_ratio: number;
}

interface EducationLevelBreakdown {
  education_level: string;
  total_schools: number;
  government_schools: number;
  private_schools: number;
  total_students: number;
  average_students_per_school: number;
}

interface SchoolSummaryData {
  report_date: string;
  filters: {
    region?: string;
    district?: string;
    school_type?: string;
  };
  overall_statistics: {
    total_schools: number;
    government_schools: number;
    private_schools: number;
    regions_covered: number;
    districts_covered: number;
    average_school_age: number;
    government_percentage: number;
    private_percentage: number;
  };
  district_breakdown: DistrictBreakdown[];
  education_level_breakdown: EducationLevelBreakdown[];
  enrollment_analysis: {
    total_enrollment: number;
    average_enrollment_per_school: number;
    min_enrollment: number;
    max_enrollment: number;
    gender_parity_index: number;
    female_percentage: number;
    male_percentage: number;
    size_distribution: {
      no_enrollment: number;
      small_schools_1_50: number;
      medium_schools_51_200: number;
      large_schools_201_500: number;
      very_large_schools_500_plus: number;
    };
  };
  teacher_analysis: {
    total_teachers: number;
    average_teachers_per_school: number;
    min_teachers: number;
    max_teachers: number;
  };
  infrastructure_overview: {
    total_schools: number;
    schools_with_establishment_data: number;
    average_school_age: number;
    oldest_school_year: number;
    newest_school_year: number;
    data_completeness_percentage: number;
  };
  performance_metrics: {
    schools_analyzed: number;
    average_student_teacher_ratio: number;
    schools_without_students: number;
    schools_without_teachers: number;
    operational_schools: number;
  };
  detailed_schools: SchoolDetail[];
}

const SchoolSummaryReport: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedSchoolType, setSelectedSchoolType] = useState<string>('');

  const { data: reportData, isLoading, error, refetch } = useQuery<{ data: SchoolSummaryData }>({
    queryKey: ['school-summary-report', selectedRegion, selectedDistrict, selectedSchoolType],
    queryFn: async () => {
      const token = localStorage.getItem('emis_auth_token');
      const params = new URLSearchParams();
      if (selectedRegion) params.append('region', selectedRegion);
      if (selectedDistrict) params.append('district', selectedDistrict);
      if (selectedSchoolType) params.append('school_type', selectedSchoolType);
      
      const response = await axios.get(
        `${API_URL}/sis-integration/school-summary-reports/?${params.toString()}`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const getSchoolSizeColor = (size: number) => {
    if (size >= 500) return 'success';
    if (size >= 200) return 'info';
    if (size >= 50) return 'warning';
    return 'error';
  };

  const getStudentTeacherRatioColor = (ratio: number) => {
    if (ratio <= 25) return 'success';
    if (ratio <= 35) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reportData) {
    return (
      <Alert severity="error">
        Failed to load school summary report. Please try again.
      </Alert>
    );
  }

  const report = reportData.data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Comprehensive School Summary Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<CompareIcon />}
            color="info"
          >
            Compare Schools
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            color="primary"
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion}
                  label="Region"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  <MenuItem value="Unguja">Unguja</MenuItem>
                  <MenuItem value="Pemba">Pemba</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  label="District"
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {/* District options would be populated dynamically */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>School Type</InputLabel>
                <Select
                  value={selectedSchoolType}
                  label="School Type"
                  onChange={(e) => setSelectedSchoolType(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="Government">Government</MenuItem>
                  <MenuItem value="Private">Private</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Overall Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SchoolIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.overall_statistics.total_schools.toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total Schools
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`${report.overall_statistics.government_percentage}% Government`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.enrollment_analysis.total_enrollment.toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total Students
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`GPI: ${report.enrollment_analysis.gender_parity_index}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PersonIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.teacher_analysis.total_teachers.toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total Teachers
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`Avg: ${report.teacher_analysis.average_teachers_per_school}/school`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.performance_metrics.average_student_teacher_ratio}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Avg Student:Teacher Ratio
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`${report.performance_metrics.operational_schools} operational`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* District Breakdown */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">District Analysis</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>District</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Schools</TableCell>
                  <TableCell align="right">Students</TableCell>
                  <TableCell align="right">Teachers</TableCell>
                  <TableCell align="center">Student:Teacher Ratio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.district_breakdown.map((district, index) => (
                  <TableRow key={index}>
                    <TableCell>{district.district_name}</TableCell>
                    <TableCell>{district.region_name}</TableCell>
                    <TableCell align="right">
                      {district.total_schools}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {district.government_schools}G / {district.private_schools}P
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {district.total_students.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {district.total_teachers.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={district.student_teacher_ratio}
                        size="small"
                        color={getStudentTeacherRatioColor(district.student_teacher_ratio)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Education Level Breakdown */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Education Level Distribution</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {report.education_level_breakdown.map((level, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {level.education_level}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Schools: {level.total_schools}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Students: {level.total_students.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg per School: {level.average_students_per_school}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption">
                        {level.government_schools}G / {level.private_schools}P
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* School Size Distribution */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">School Size Analysis</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Enrollment Distribution
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      No Enrollment: {report.enrollment_analysis.size_distribution.no_enrollment}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(report.enrollment_analysis.size_distribution.no_enrollment / report.overall_statistics.total_schools) * 100}
                      color="error"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Small (1-50): {report.enrollment_analysis.size_distribution.small_schools_1_50}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(report.enrollment_analysis.size_distribution.small_schools_1_50 / report.overall_statistics.total_schools) * 100}
                      color="warning"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Medium (51-200): {report.enrollment_analysis.size_distribution.medium_schools_51_200}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(report.enrollment_analysis.size_distribution.medium_schools_51_200 / report.overall_statistics.total_schools) * 100}
                      color="info"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Large (201-500): {report.enrollment_analysis.size_distribution.large_schools_201_500}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(report.enrollment_analysis.size_distribution.large_schools_201_500 / report.overall_statistics.total_schools) * 100}
                      color="success"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      Very Large (500+): {report.enrollment_analysis.size_distribution.very_large_schools_500_plus}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(report.enrollment_analysis.size_distribution.very_large_schools_500_plus / report.overall_statistics.total_schools) * 100}
                      color="success"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Infrastructure Overview
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Average School Age: {report.infrastructure_overview.average_school_age} years
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Oldest School: {report.infrastructure_overview.oldest_school_year}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Newest School: {report.infrastructure_overview.newest_school_year}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Data Completeness: {report.infrastructure_overview.data_completeness_percentage}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={report.infrastructure_overview.data_completeness_percentage}
                      color={report.infrastructure_overview.data_completeness_percentage > 80 ? 'success' : 'warning'}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Top Schools */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Top Schools by Enrollment</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell>Education Level</TableCell>
                  <TableCell align="right">Students</TableCell>
                  <TableCell align="right">Teachers</TableCell>
                  <TableCell align="center">Ratio</TableCell>
                  <TableCell align="right">Est. Year</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.detailed_schools.slice(0, 20).map((school, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Tooltip title={school.school_registration_number}>
                        <Typography variant="body2">
                          {school.school_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={school.school_type}
                        size="small"
                        color={school.school_type === 'Government' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{school.district}</TableCell>
                    <TableCell>{school.education_level}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={school.total_students.toLocaleString()}
                        size="small"
                        color={getSchoolSizeColor(school.total_students)}
                      />
                    </TableCell>
                    <TableCell align="right">{school.total_teachers}</TableCell>
                    <TableCell align="center">
                      {school.student_teacher_ratio > 0 ? (
                        <Chip
                          label={school.student_teacher_ratio}
                          size="small"
                          color={getStudentTeacherRatioColor(school.student_teacher_ratio)}
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">{school.establishment_year}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Report Metadata */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Information
          </Typography>
          <Typography variant="body2">
            Report Generated: {new Date(report.report_date).toLocaleDateString()}
          </Typography>
          <Typography variant="body2">
            Data Source: Real SIS Database
          </Typography>
          <Typography variant="body2">
            Geographic Coverage: {report.overall_statistics.regions_covered} regions, {report.overall_statistics.districts_covered} districts
          </Typography>
          {report.filters.region && (
            <Typography variant="body2">
              Region Filter: {report.filters.region}
            </Typography>
          )}
          {report.filters.district && (
            <Typography variant="body2">
              District Filter: {report.filters.district}
            </Typography>
          )}
          {report.filters.school_type && (
            <Typography variant="body2">
              School Type Filter: {report.filters.school_type}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolSummaryReport;